// client/src/features/properties/search.ts — SCR-PROP-001's URL state · PROP-004 (04 §9.2.3).
//
// §9.2.3 is NORMATIVE for every LIST screen: filters, pagination and sort live in the URL as
// Zod-validated search params, so a filtered list is shareable, survives refresh, and browser
// Back is a filter-undo. `routes/demo.tsx` proves the mechanism; this is the first real screen
// to use it, and it copies that shape rather than inventing one.
//
// The five filters are the five `GET /api/v1/portfolio/properties` takes (ARCH-008) — `q`,
// `propertyType`, `county`, `occupancy`, `buildingRef` — spelled as the contract spells them,
// so `queries.ts` can hand the search straight to the generated `query` object.
//
// The schema lives here rather than in the route file because two things need it: the route
// (as `validateSearch`) and the list component (as the type of what it reads back).

import { z } from 'zod';

import { OCCUPANCY_STATUSES, PROPERTY_TYPES } from './enums';

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const propertySearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().optional(),
  q: z.string().trim().optional(),
  propertyType: z.enum(PROPERTY_TYPES).optional(),
  occupancy: z.enum(OCCUPANCY_STATUSES).optional(),
  county: z.string().trim().optional(),
  /** §8.3's group-by / filter control. Absent unless the portfolio actually uses `building_ref`. */
  buildingRef: z.string().trim().optional(),
});

export type PropertySearch = z.infer<typeof propertySearchSchema>;

/** Which filters are engaged — the empty state needs to know why it is empty. */
export function hasActiveFilters(search: PropertySearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.propertyType !== undefined ||
    search.occupancy !== undefined ||
    (search.county !== undefined && search.county !== '') ||
    (search.buildingRef !== undefined && search.buildingRef !== '')
  );
}

/** Clearing filters returns to page 1: page 3 of a set that no longer exists renders nothing. */
export const CLEARED_SEARCH: PropertySearch = { page: 1, pageSize: 25 };
